import { useEffect } from 'react';

interface SEOStructuredDataProps {
  type?: 'WebPage' | 'FAQPage' | 'LocalBusiness';
}

const SEOStructuredData: React.FC<SEOStructuredDataProps> = ({ type = 'WebPage' }) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // LocalBusiness Schema
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "萬業人才資本 Omni Workforce",
      "description": "一站式粵港外勞招聘服務、香港移民等全方位人才入境服務，持有香港職業介紹所牌照80063",
      "url": siteUrl,
      "telephone": "+852-9366-3961",
      "email": "omnisourcetalent@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "11/F, TIMES MEDIA CENTRE, 133 WANCHAI ROAD",
        "addressLocality": "WANCHAI",
        "addressRegion": "HONG KONG",
        "postalCode": "",
        "addressCountry": "HK"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 22.2783,
        "longitude": 114.1747
      },
      "openingHours": "Mo-Fr 09:00-18:00",
      "priceRange": "$$",
      "serviceArea": {
        "@type": "Place",
        "name": "香港特別行政區"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "人才入境服務",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "外勞配額申請",
              "description": "專業協助申請香港外勞配額"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "香港移民服務",
              "description": "高才通、專才、優才及投資移民等服務"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "外籍家庭傭工招聘",
              "description": "菲傭、印傭等外籍家庭傭工招聘服務"
            }
          }
        ]
      }
    };

    // FAQPage Schema for home page
    const homeFAQSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "輸入外勞工資標準是多少？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "根據勞工處《主要職位及每月中位工資一覽表》，僱主提供的薪金需不低於市場同類職位的中位工資。如某些職位無中位工資數據，申請時主任會提供合適薪資範圍供參考。"
          }
        },
        {
          "@type": "Question",
          "name": "餐飲集團如何申請？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "可使用旗下子公司的名義申請，需提交全部分公司名單，供主任審核確認。"
          }
        },
        {
          "@type": "Question",
          "name": "輸入外勞有國籍限制嗎？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "本計劃對外勞國籍無限制，但需注意當地出境手續。如有特殊需求，我們可提供協助。本公司在菲律賓設有招聘辦公室，隨時支援。"
          }
        },
        {
          "@type": "Question",
          "name": "僱主需提供住宿嗎？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "僱主可自願提供居所，並從薪金中扣除最多10%作為住宿費。外勞亦可選擇住在內地居所，靈活安排。"
          }
        },
        {
          "@type": "Question",
          "name": "可否同時申請多個工作地點？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "當然可以，僱主可填寫多個不同工作地點，滿足業務需求。"
          }
        },
        {
          "@type": "Question",
          "name": "若本地招聘無合適人選，會否影響配額申請？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "招聘期結束後，需向補充勞工科提交本地招聘結果及合理解釋，符合要求不會影響後續配額申請。"
          }
        },
        {
          "@type": "Question",
          "name": "若自行找到合適人選，可否找貴公司處理流程？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "可以！配額批出後，我們合作的內地指定勞務公司會聯繫外勞辦理簽證。根據《對外勞務合作管理條例》，勞務公司會收取相關費用。"
          }
        },
        {
          "@type": "Question",
          "name": "勞工辭職或解僱如何處理？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "按香港勞工法例辦理，通知期依據合約中約定時間執行。"
          }
        }
      ]
    };

    // FAQPage Schema for immigration page
    const immigrationFAQSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "什麼是高才通計劃？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "高端人才通行證計劃（高才通）是香港政府推出的人才引進計劃，旨在吸引全球高端人才來港發展。符合條件的申請人可獲發為期兩年的通行證，無需事先獲得本地僱主聘用。"
          }
        },
        {
          "@type": "Question",
          "name": "申請香港專才計劃需要什麼條件？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "申請人需具備香港缺乏或無法即時提供的專業知識和技能，已獲得本地僱主聘用，薪酬福利須與市場水平相若，並且無犯罪記錄。"
          }
        },
        {
          "@type": "Question",
          "name": "優才計劃的評分標準是什麼？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "優才計劃採用綜合計分制或成就計分制。綜合計分制根據年齡、學歷、工作經驗、語言能力和家庭背景等因素評分，滿分245分，達到80分即可申請。"
          }
        },
        {
          "@type": "Question",
          "name": "香港投資移民需要投資多少金額？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "新資本投資者入境計劃要求申請人在香港投資至少3000萬港元於指定資產類別，包括股票、基金、債券等金融資產或創新科技企業。"
          }
        },
        {
          "@type": "Question",
          "name": "申請香港移民需要多長時間？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "處理時間因計劃而異。高才通一般4-6週，專才計劃約4-6週，優才計劃約6-9個月，投資移民約3-6個月。我們會協助加快處理流程。"
          }
        },
        {
          "@type": "Question",
          "name": "獲批後可以帶家人一起來香港嗎？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "可以。獲批准的申請人可以攜同配偶及18歲以下未婚子女來港，受養人可在港就學和工作。"
          }
        }
      ]
    };

    // WebPage Schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": type === 'WebPage' && window.location.pathname.includes('immigration') 
        ? "香港移民服務 - 萬業人才資本" 
        : "萬業人才資本 | 香港輸入外勞及香港移民服務",
      "description": type === 'WebPage' && window.location.pathname.includes('immigration')
        ? "提供高才通、專才、優才及投資移民等香港移民服務，專業團隊協助您順利移居香港"
        : "萬業人才資本提供一站式粵港外勞招聘服務、香港移民等全方位人才入境服務",
      "url": siteUrl + window.location.pathname,
      "publisher": {
        "@type": "Organization",
        "name": "萬業人才資本 Omni Workforce",
        "url": siteUrl
      }
    };

    // Add schemas to head
    const addSchema = (schema: object, id: string) => {
      const existingScript = document.getElementById(id);
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    };

    // Always add LocalBusiness schema
    addSchema(localBusinessSchema, 'local-business-schema');

    // Add appropriate schema based on type
    if (type === 'FAQPage') {
      if (window.location.pathname.includes('immigration')) {
        addSchema(immigrationFAQSchema, 'faq-schema');
      } else {
        addSchema(homeFAQSchema, 'faq-schema');
      }
    } else if (type === 'WebPage') {
      addSchema(webPageSchema, 'webpage-schema');
    }

    // Cleanup function
    return () => {
      const localBusinessScript = document.getElementById('local-business-schema');
      const faqScript = document.getElementById('faq-schema');
      const webpageScript = document.getElementById('webpage-schema');
      if (localBusinessScript) localBusinessScript.remove();
      if (faqScript) faqScript.remove();
      if (webpageScript) webpageScript.remove();
    };
  }, [type]);

  return null;
};

export default SEOStructuredData;